import { TokenManager } from '../auth/TokenManager';
import { mayRecalculateFor, officeReady, workbookCompany } from './workbookCompany';
import { allCellsSetOffSince, paneActionRunning } from './paneActivity';
import { recalculateThisWorkbook } from './workbookRecalc';

/**
 * Cells that answered #SIGN_IN are not told when the user signs in, which may happen in another
 * workbook's pane: the functions are not volatile, and the pane recalculates only its own workbook.
 * This runtime remembers that a cell answered so, looks for a new session every few seconds, and when
 * one appears recalculates this workbook once.
 *
 * Only a session other than the one there was when the cell answered ends the wait. A cell also
 * answers #SIGN_IN when a stored session cannot be renewed right now; recalculating for that same
 * session would ask the server again every few seconds for as long as it refuses.
 */

/** How often a cell waiting for a session looks for one; a storage read. */
const LOOK_EVERY_MS = 7500;
/** How long it waits before trying again after Excel refused the recalculation, by failures so far. */
const BACK_OFF_MS = [15_000, 60_000];
const LONGEST_BACK_OFF_MS = 300_000;

interface Waiting {
  /** When a cell last answered #SIGN_IN. */
  since: number;
  /** A mark of the session there was then; undefined until read, null for none. */
  mark?: number | null;
  /** That session's company; null when there was no session. Read with `mark`. */
  companyId?: string | null;
}

interface Recovery {
  waiting?: Waiting;
  timer?: ReturnType<typeof setInterval>;
  looking?: boolean;
  failures?: number;
  /** No look before this, after a recalculation Excel refused. */
  retryAt?: number;
}

interface RecoveryHolder {
  __bifrostSignInRecovery?: Recovery;
}

/** On globalThis because the functions bundle notes the answers and the task-pane bundle looks too. */
function recovery(): Recovery {
  const holder = globalThis as unknown as RecoveryHolder;
  if (!holder.__bifrostSignInRecovery) holder.__bifrostSignInRecovery = {};
  return holder.__bifrostSignInRecovery;
}

let tokenManager: TokenManager | null = null;

function tokens(): TokenManager {
  if (!tokenManager) tokenManager = new TokenManager();
  return tokenManager;
}

/** Not a secret: only tells one stored session from the next, so the session itself is not kept here. */
function hash(text: string): number {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return h;
}

async function sessionMark(): Promise<number | null> {
  const stored = await tokens().storedSession();
  return stored === null ? null : hash(stored);
}

/** The stored session's mark, and its company, or nulls when there is none. */
async function currentSession(): Promise<{ mark: number | null; companyId: string | null }> {
  const mark = await sessionMark();
  if (mark === null) return { mark, companyId: null };
  return { mark, companyId: (await tokens().getUserInfo())?.companyId ?? null };
}

function stopWaiting(waiting: Waiting): void {
  const state = recovery();
  if (state.waiting !== waiting) return;
  state.waiting = undefined;
  if (state.timer) clearInterval(state.timer);
  state.timer = undefined;
}

/** A cell answered #SIGN_IN: this workbook waits for a session. */
export function noteSignInAnswered(): void {
  const state = recovery();
  const waiting: Waiting = { since: Date.now() };
  state.waiting = waiting;
  currentSession()
    .then(({ mark, companyId }) => {
      waiting.mark = mark;
      waiting.companyId = companyId;
    })
    .catch(() => {
      waiting.mark = null;
      waiting.companyId = null;
    });
  if (!state.timer) state.timer = setInterval(() => void lookForSignIn(), LOOK_EVERY_MS);
}

/**
 * An unbound workbook is bound by the first request its recalculation makes. It may recover only into
 * the company its cells answered for, or, where they answered with no session at all, into whichever
 * the user signs in to: a switch made meanwhile in another workbook's pane does not choose its company.
 */
function mayBindTo(waiting: Waiting, companyId: string | null | undefined): boolean {
  return workbookCompany() !== null || waiting.companyId === null || waiting.companyId === companyId;
}

/**
 * Whether a session has appeared since a cell answered #SIGN_IN; if so, and recalculating computes
 * the cells for the company this workbook belongs to, recalculates this workbook. Every few seconds
 * while a cell waits, and on a sheet's activation.
 */
export async function lookForSignIn(): Promise<void> {
  const state = recovery();
  const waiting = state.waiting;
  if (!waiting || state.looking) return;
  // Every cell was asked again since, and one still without a session has said so again.
  if (allCellsSetOffSince(waiting.since)) {
    stopWaiting(waiting);
    return;
  }
  if (waiting.mark === undefined || paneActionRunning()) return;
  if (state.retryAt !== undefined && Date.now() < state.retryAt) return;
  state.looking = true;
  try {
    const mark = await sessionMark();
    if (mark === null || mark === waiting.mark) return;
    const user = await tokens().getUserInfo();
    await officeReady();
    // Another company's workbook keeps its saved figures; a switch back recalculates it.
    if (!mayRecalculateFor(user?.companyId) || !mayBindTo(waiting, user?.companyId)) return;
    // It sets every cell off, which ends the wait at the next look; a cell that still has no session
    // answers #SIGN_IN after that, and waits again for the session after this one.
    await recalculateThisWorkbook();
    state.failures = 0;
    state.retryAt = undefined;
  } catch (error) {
    console.warn('Could not recalculate the workbook after signing in:', error);
    const failures = (state.failures ?? 0) + 1;
    state.failures = failures;
    state.retryAt = Date.now() + (BACK_OFF_MS[failures - 1] ?? LONGEST_BACK_OFF_MS);
  } finally {
    state.looking = false;
  }
}
